// ── Slack App Manifest Generator ─────────────────────────────────────────────
// Generates a Slack app manifest object from agent metadata and selected scopes.
// The manifest can be serialized to YAML for use with `slack create --manifest`.

import type { SlackScope, SlackAppManifest } from '../types/channel-config.js';
import { getSlackScopeDefinition } from './slack-scopes.js';

export interface SlackManifestInput {
  /** Agent display name (used as Slack app name). */
  agent_name: string;
  /** Optional short description (max 140 chars). */
  description?: string;
  /** Optional long description / agent description (max 4,000 chars). */
  long_description?: string;
  /** Bot scopes to request. */
  scopes: SlackScope[];
  /** Whether to enable Socket Mode (default: true). */
  socket_mode?: boolean;
  /** OAuth redirect URLs (required for OAuth install flow). */
  redirect_urls?: string[];
  /**
   * ENG-4573: URL Slack POSTs interactive payloads to. When provided,
   * the generated manifest sets `settings.interactivity.is_enabled = true`
   * + `request_url`. Omit it to leave interactivity off (the existing
   * default for apps that don't use Block Kit yet).
   */
  interactivity_request_url?: string;
  /**
   * ENG-4596: URL Slack POSTs slash-command payloads to. When provided
   * AND the `commands` scope is requested, the manifest registers the
   * per-agent slash commands pointing at this URL. Omit to leave
   * existing apps unchanged on re-provision.
   */
  slash_command_url?: string;
  /**
   * ENG-6044: kebab-case agent `code_name` used to suffix the per-agent
   * slash commands — `/status-<code-name>` (ENG-6233, was /agent-status),
   * `/help-<code-name>`, `/restart-<code-name>`,
   * `/investigate-<code-name>` — so multiple agents installed in one
   * workspace don't register colliding command names (Slack's command
   * picker shows identical duplicate entries otherwise). Omit to keep
   * the legacy generic names.
   */
  agent_code_name?: string;
}

/**
 * Slack rejects slash-command names longer than 32 characters.
 * https://api.slack.com/interactivity/slash-commands#creating_commands
 */
const SLACK_COMMAND_MAX_LENGTH = 32;

/**
 * ENG-6044: compose a per-agent slash-command name — `<base>-<code-name>`
 * — falling back to the unsuffixed base when no (valid kebab-case) code
 * name is supplied or the suffixed name would exceed Slack's 32-char
 * limit. Fallback over truncation: a truncated suffix would mismatch
 * what the envelope handler in packages/mcp/src/slack-channel.ts expects
 * (it composes the same `<base>-<code-name>` from AGT_AGENT_CODE_NAME —
 * keep the two implementations in sync) and the command would go
 * unrouted.
 */
export function agentSlashCommand(base: string, codeName?: string | null): string {
  if (!codeName) return base;
  const slug = codeName.trim().toLowerCase();
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) return base;
  const suffixed = `${base}-${slug}`;
  return suffixed.length > SLACK_COMMAND_MAX_LENGTH ? base : suffixed;
}

/**
 * Maps bot scopes to the Slack event subscriptions they require.
 * Only scopes that imply specific events are listed here.
 *
 * Reference: https://api.slack.com/events
 */
const SCOPE_TO_EVENTS: Partial<Record<SlackScope, string[]>> = {
  'app_mentions:read': ['app_mention'],
  'assistant:write': ['assistant_thread_started'],
  'channels:history': ['message.channels'],
  'channels:read': ['channel_rename', 'member_joined_channel', 'member_left_channel'],
  'groups:history': ['message.groups'],
  'groups:read': ['member_joined_channel', 'member_left_channel'],
  'im:history': ['message.im'],
  // im_created is a user-scope event, not valid for bot_events — omit it
  // 'im:read': ['im_created'],
  'mpim:history': ['message.mpim'],
  'mpim:read': ['member_joined_channel'],
  'reactions:read': ['reaction_added', 'reaction_removed'],
  'pins:read': ['pin_added', 'pin_removed'],
  'metadata.message:read': ['message_metadata_posted'],
};

/**
 * Generate a Slack App Manifest from agent info and selected scopes.
 *
 * The manifest follows the Slack App Manifest schema:
 * https://api.slack.com/reference/manifests
 */
export function generateSlackAppManifest(input: SlackManifestInput): SlackAppManifest {
  const {
    agent_name,
    description,
    long_description,
    scopes,
    socket_mode = true,
    redirect_urls,
    interactivity_request_url,
    slash_command_url,
    agent_code_name,
  } = input;

  // Derive bot display name (max 35 chars for Slack)
  const botDisplayName = agent_name.length > 35
    ? agent_name.slice(0, 35)
    : agent_name;

  // Collect bot events from selected scopes
  const botEvents = new Set<string>();
  for (const scope of scopes) {
    const events = SCOPE_TO_EVENTS[scope];
    if (events) {
      for (const event of events) {
        botEvents.add(event);
      }
    }
  }

  const manifest: SlackAppManifest = {
    display_information: {
      name: agent_name,
      ...(description ? { description: description.slice(0, 140) } : {}),
      ...(long_description && long_description.length >= 175 ? { long_description: long_description.slice(0, 4000) } : {}),
    },
    features: {
      app_home: {
        home_tab_enabled: false,
        messages_tab_enabled: true,
        messages_tab_read_only_enabled: false,
      },
      bot_user: {
        display_name: botDisplayName,
        always_online: true,
      },
      // ENG-4596: register the per-agent slash commands when the caller
      // passed a URL AND the app requested the `commands` scope. Slack
      // rejects manifests where slash_commands is non-empty without the
      // matching scope, so the scope check is a guard.
      //
      // ENG-5150: register /restart. The slash_commands envelope handler
      // in packages/mcp/src/slack-channel.ts already routes it; without the
      // manifest entry Slack treats typed `/restart` as a plain message and
      // posts it to the channel before the bot can intercept it.
      //
      // ENG-6233: bare `/help` can't be registered — Slack reserves it as a
      // built-in global command — so we register the per-agent `/help-<code>`
      // instead (a non-reserved name) to get `/`-autocomplete discovery. The
      // message-intercept fallback in slack-channel.ts still handles a typed
      // bare `/help` for muscle memory.
      //
      // ENG-6044: the per-agent commands carry the agent code-name suffix
      // (/status-don, /help-don) so multiple agents in one workspace don't
      // register colliding names. /debug is renamed /investigate-<code-name>
      // in the same move; the envelope handler still routes legacy names
      // (including the pre-ENG-6233 /agent-status-<code>) during migration.
      ...(slash_command_url && scopes.includes('commands')
        ? {
            slash_commands: [
              {
                command: agentSlashCommand('/status', agent_code_name),
                url: slash_command_url,
                description: "This agent's model, session origin, uptime + connectivity.",
                should_escape: false,
              },
              // ENG-6931: /ping-<code> - a connectivity check. The agent posts a
              // visible pong via the normal chat.postMessage reply path, proving
              // the channel can actually deliver. Routed by the slash_commands
              // envelope handler in packages/mcp/src/slack-channel.ts; gated on the
              // ping allowlist (team members + reports-to manager) materialized as
              // SLACK_PING_ALLOWED_USERS.
              {
                command: agentSlashCommand('/ping', agent_code_name),
                url: slash_command_url,
                description: 'Ping this agent to confirm its channel is connected (team + manager only).',
                should_escape: false,
              },
              // ENG-6233: per-agent /help-<code>. Bare `/help` is Slack-reserved,
              // so register ONLY when a valid code name actually suffixes it
              // (agentSlashCommand returns the bare base when it can't suffix —
              // no code name, non-kebab, or over the 32-char limit). The typed
              // bare-`/help` message-intercept in slack-channel.ts covers the
              // unsuffixed case.
              ...(agentSlashCommand('/help', agent_code_name) !== '/help'
                ? [
                    {
                      command: agentSlashCommand('/help', agent_code_name),
                      url: slash_command_url,
                      description: 'List this agent’s available commands.',
                      should_escape: false,
                    },
                  ]
                : []),
              {
                command: agentSlashCommand('/restart', agent_code_name),
                url: slash_command_url,
                description: 'Restart this agent (allowlisted users only).',
                should_escape: false,
              },
              // ENG-6511: re-run self-onboarding (re-interview; the agent's
              // config is kept, not wiped, ENG-6531). Routed by the
              // slash_commands envelope handler in
              // packages/mcp/src/slack-channel.ts, which forwards to
              // POST /host/onboarding/reset (RESET — clears the progress trail).
              {
                command: agentSlashCommand('/onboard', agent_code_name),
                url: slash_command_url,
                description: 'Re-run this agent’s onboarding interview, keeping its existing config (allowlisted users only).',
                should_escape: false,
              },
              // ENG-6490 / ENG-6511: resume self-onboarding from where it left
              // off. Routed by the slash_commands envelope handler in
              // packages/mcp/src/slack-channel.ts, which forwards to
              // POST /host/onboarding/resume (RESUME — preserves progress).
              {
                command: agentSlashCommand('/resume-onboarding', agent_code_name),
                url: slash_command_url,
                description: 'Resume this agent’s onboarding where it left off (allowlisted users only).',
                should_escape: false,
              },
              // ENG-6030: live pane tail. Routed by the slash_commands
              // envelope handler in packages/mcp/src/slack-channel.ts;
              // fail-closed (DM + non-empty SLACK_ALLOWED_USERS required).
              // ENG-6044: renamed from /debug.
              {
                command: agentSlashCommand('/investigate', agent_code_name),
                url: slash_command_url,
                description: "Live tail of this agent's terminal pane (DM only, allowlisted users).",
                usage_hint: 'invoke in a DM with the agent',
                should_escape: false,
              },
            ],
          }
        : {}),
    },
    oauth_config: {
      ...(redirect_urls && redirect_urls.length > 0 ? { redirect_urls } : {}),
      // ENG-4812: partition by token_type so user-only scopes
      // (e.g. users.profile:write) don't end up under `bot` and
      // trigger Slack's `illegal_bot_scopes` rejection. Scopes
      // without an explicit token_type default to 'bot' — matches
      // pre-fix behaviour for the registry's standard-token-set.
      scopes: (() => {
        const botScopes: SlackScope[] = [];
        const userScopes: SlackScope[] = [];
        for (const scope of scopes) {
          const def = getSlackScopeDefinition(scope);
          if (def?.token_type === 'user') userScopes.push(scope);
          else botScopes.push(scope);
        }
        return userScopes.length > 0
          ? { bot: botScopes, user: userScopes }
          : { bot: botScopes };
      })(),
    },
    settings: {
      ...(botEvents.size > 0
        ? { event_subscriptions: { bot_events: [...botEvents].sort() } }
        : {}),
      // ENG-4573: opt-in interactivity. Only emit the block when the
      // caller passed a request_url so existing apps that don't use
      // Block Kit re-provision unchanged.
      ...(interactivity_request_url
        ? {
            interactivity: {
              is_enabled: true,
              request_url: interactivity_request_url,
            },
          }
        : {}),
      socket_mode_enabled: socket_mode,
      org_deploy_enabled: false,
      token_rotation_enabled: false,
    },
  };

  return manifest;
}

/**
 * Serialize a Slack App Manifest to a YAML-compatible plain object.
 * The returned object uses the `_metadata.major_version` key that
 * Slack expects at the top level.
 */
export function serializeManifestForSlackCli(manifest: SlackAppManifest): Record<string, unknown> {
  return {
    _metadata: { major_version: 2 },
    ...manifest,
  };
}
